import { Link, useNavigate } from 'react-router-dom';
import type { IsEmriOzet, KasaHareketiOzet, KasaOzeti, Musteri, Urun } from '@nero/shared';
import { ARACSIZ, bugun, para, plakaGoster, tarihGoster } from '@nero/shared';
import { useVeri } from '../lib/veri.js';
import { useOturum } from '../lib/oturum.js';
import { BosDurum, HataKutusu, Kart, KartBaslik, Olcum, Rozet, SayfaBaslik, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { Simge } from '../bilesenler/Simge.js';
import { DurumRozeti, OdemeRozeti } from './ortak/rozetler.js';

interface Ozet {
  acikIsler: IsEmriOzet[];
  /** Açık iş listesi 100'de kesilir; sayaç gerçek toplamı gösterir. */
  acikIsSayisi?: number;
  bugunAcilan: number;
  kritikStok: Urun[];
  /* Stok DEĞERİ yalnızca maliyet izni olana gönderilir; personelde bu
     alanlar hiç gelmez. */
  stokOzeti: { cesit: number; satisDegeri?: number; alisDegeri?: number; kritik: number; tukenen: number };
  bugunKasa?: KasaOzeti;
  bugunHareketler?: KasaHareketiOzet[];
  ayKasa?: KasaOzeti;
  isEmriOzeti?: { acik: number; tamamlanan: number; ciro: number; tahsilat: number; bekleyenTahsilat: number };
  borclular?: Array<Musteri & { bakiye: number }>;
  /** Borçlu listesi ilk 10'la sınırlı; rozet gerçek toplamı gösterir. */
  borcluSayisi?: number;
}

function selamlama(): string {
  const s = new Date().getHours();
  if (s < 6) return 'İyi geceler';
  if (s < 12) return 'Günaydın';
  if (s < 18) return 'İyi günler';
  return 'İyi akşamlar';
}

export function AnaSayfa() {
  const { kullanici, yetkili } = useOturum();
  const { veri, yukleniyor, hata, yenile } = useVeri<Ozet>('/ozet');
  const git = useNavigate();
  const yonetici = yetkili('kasa.goruntule');

  if (hata) return <div className="sayfa"><HataKutusu hata={hata} yenile={yenile} /></div>;
  if (yukleniyor || !veri) return <div className="sayfa"><Yukleniyor satir={5} /></div>;

  const ilkAd = kullanici?.ad_soyad?.split(' ')[0] ?? '';

  return (
    <div className="sayfa">
      <SayfaBaslik
        baslik={`${selamlama()}, ${ilkAd}`}
        alt={`Bugün ${tarihGoster(bugun())} · ${veri.bugunAcilan} iş emri açıldı`}
      >
        {yetkili('is_emri.olustur') && (
          <Link className="btn btn-birincil" to="/is-emirleri?yeni=1">
            <Simge ad="arti" className="simge" /> Yeni iş emri
          </Link>
        )}
      </SayfaBaslik>

      {veri.kritikStok.length > 0 && yetkili('stok.goruntule') && (
        <div style={{ marginBottom: 'var(--b5)' }}>
          <UyariSerit
            ton="uyari"
            baslik={`${veri.kritikStok.length} ürün kritik seviyede`}
            eylem={<Link className="btn btn-ikincil btn-kucuk" to="/stok">Stoğa git</Link>}
          >
            {veri.kritikStok.slice(0, 4).map((u) => u.ad).join(' · ')}
            {veri.kritikStok.length > 4 && ` ve ${veri.kritikStok.length - 4} ürün daha`}
          </UyariSerit>
        </div>
      )}

      <div className="izgara izgara-4">
        <Olcum etiket="Açık iş" deger={veri.acikIsSayisi ?? veri.acikIsler.length} alt="Bekleyen ve devam eden" buyuk />
        {yonetici && veri.bugunKasa && (
          <>
            <Olcum etiket="Bugün girdi" deger={para(veri.bugunKasa.girdi)} alt={`${veri.bugunKasa.adet} hareket`} vurgu="olumlu" />
            <Olcum etiket="Bugün çıktı" deger={para(veri.bugunKasa.cikti)} />
            <Olcum etiket="Bugün kasa" deger={para(veri.bugunKasa.net)} buyuk
              vurgu={veri.bugunKasa.net >= 0 ? 'olumlu' : 'uyari'} alt="Net" />
          </>
        )}
        {yonetici && veri.ayKasa && (
          <Olcum etiket="Bu ay net" deger={para(veri.ayKasa.net)} alt={`Girdi ${para(veri.ayKasa.girdi)} · Gider ${para(veri.ayKasa.cikti)}`} />
        )}
        {yonetici && veri.isEmriOzeti && (
          <Olcum
            etiket="Bekleyen tahsilat"
            deger={para(veri.isEmriOzeti.bekleyenTahsilat)}
            alt="Bu ay açılan işlerden"
            vurgu={veri.isEmriOzeti.bekleyenTahsilat > 0 ? 'uyari' : undefined}
          />
        )}
        {!yonetici && (
          <>
            <Olcum etiket="Bugün açılan" deger={veri.bugunAcilan} alt="Yeni iş emri" />
            <Olcum etiket="Kritik stok" deger={veri.kritikStok.length} alt="Yenilenmesi gereken ürün" vurgu={veri.kritikStok.length ? 'uyari' : undefined} />
          </>
        )}
      </div>

      <div className="bolum">
        <Kart>
          <KartBaslik baslik="Atölyedeki araçlar">
            <Link className="btn btn-sade btn-kucuk" to="/is-emirleri">
              Tümü <Simge ad="ok-sag" boyut={14} />
            </Link>
          </KartBaslik>
          {veri.acikIsler.length === 0 ? (
            <BosDurum
              simge="arac"
              baslik="Atölyede açık iş yok"
              aciklama="Araç geldiğinde yeni iş emri açarak takibi başlatabilirsiniz."
            >
              {yetkili('is_emri.olustur') && (
                <Link className="btn btn-ikincil" to="/is-emirleri?yeni=1">Yeni iş emri</Link>
              )}
            </BosDurum>
          ) : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead>
                  <tr>
                    <th>Araç</th>
                    <th>Müşteri</th>
                    <th>İşlem</th>
                    <th>Durum</th>
                    <th className="sag">Tutar</th>
                    <th>Ödeme</th>
                  </tr>
                </thead>
                <tbody>
                  {veri.acikIsler.slice(0, 12).map((i) => (
                    <tr key={i.id} className="tiklanabilir" onClick={() => git(`/is-emirleri/${i.id}`)}>
                      <td>
                        <div className="ana">{plakaGoster(i.plaka) || ARACSIZ}</div>
                        <div className="ikincil">{i.marka} {i.model}</div>
                      </td>
                      <td className="ikincil">{i.musteri_ad}</td>
                      <td className="ikincil">{i.islem || '—'}</td>
                      <td><DurumRozeti durum={i.durum} /></td>
                      <td className="sag para">{para(i.tutar)}</td>
                      <td><OdemeRozeti durum={i.odeme_durumu} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Kart>
      </div>

      {yonetici && veri.bugunHareketler && (
        <div className="bolum">
          <Kart>
            <KartBaslik baslik="Bugünün işlemleri">
              <Link className="btn btn-sade btn-kucuk" to="/kasa">
                Günlük kasa <Simge ad="ok-sag" boyut={14} />
              </Link>
            </KartBaslik>
            {veri.bugunHareketler.length === 0 ? (
              <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
                Bugün henüz kasa hareketi yok.
              </div>
            ) : (
              <div className="tablo-sarma">
                <table className="tablo">
                  <thead>
                    <tr><th>Saat</th><th>Açıklama</th><th>Müşteri</th><th>Yöntem</th><th className="sag">Tutar</th></tr>
                  </thead>
                  <tbody>
                    {veri.bugunHareketler.slice(0, 12).map((h) => (
                      <tr key={h.id}>
                        <td className="ikincil dar">{h.saat || '—'}</td>
                        <td>
                          <div className="ana">{h.kategori || (h.tur === 'Girdi' ? 'Gelir' : 'Gider')}</div>
                          {h.not && <div className="ikincil">{h.not}</div>}
                        </td>
                        <td className="ikincil">{h.musteri_ad ?? '—'}</td>
                        <td className="ikincil">{h.odeme_tipi}</td>
                        <td className="sag para" style={{ color: h.tur === 'Girdi' ? 'var(--olumlu)' : undefined }}>
                          {h.tur === 'Girdi' ? '+' : '−'}{para(h.tutar)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Kart>
        </div>
      )}

      {yetkili('stok.goruntule') && (
        <div className="bolum">
          <Kart>
            <KartBaslik baslik="Stok durumu">
              <Link className="btn btn-sade btn-kucuk" to="/stok">
                Stok <Simge ad="ok-sag" boyut={14} />
              </Link>
            </KartBaslik>
            <div className="kart-govde izgara izgara-3">
              <Olcum etiket="Toplam çeşit" deger={veri.stokOzeti.cesit} alt="Aktif ürün" />
              {veri.stokOzeti.satisDegeri != null && (
                <Olcum etiket="Stok değeri (satış)" deger={para(veri.stokOzeti.satisDegeri)}
                  alt={`Alış ${para(veri.stokOzeti.alisDegeri ?? 0)}`} />
              )}
              <Olcum
                etiket="Kritik / tükenen"
                deger={`${veri.stokOzeti.kritik} / ${veri.stokOzeti.tukenen}`}
                vurgu={veri.stokOzeti.kritik + veri.stokOzeti.tukenen > 0 ? 'uyari' : undefined}
                alt="Yenilenmesi gereken"
              />
            </div>
          </Kart>
        </div>
      )}

      {yonetici && veri.borclular && veri.borclular.length > 0 && (
        <div className="bolum">
          <Kart>
            <KartBaslik baslik="Bekleyen alacaklar">
              <Rozet ton="uyari">{veri.borcluSayisi ?? veri.borclular.length} müşteri</Rozet>
            </KartBaslik>
            <div className="tablo-sarma">
              <table className="tablo">
                <thead>
                  <tr><th>Müşteri</th><th>Telefon</th><th className="sag">Bakiye</th></tr>
                </thead>
                <tbody>
                  {veri.borclular.map((m) => (
                    <tr key={m.id} className="tiklanabilir" onClick={() => git(`/musteriler/${m.id}`)}>
                      <td className="ana">{m.ad}</td>
                      <td className="ikincil">{m.telefon || '—'}</td>
                      <td className="sag para" style={{ color: 'var(--uyari)' }}>{para(m.bakiye)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Kart>
        </div>
      )}
    </div>
  );
}
